import { UntypedFormControl, Validators } from '@angular/forms';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, signal } from '@angular/core';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { SelectItem } from 'primeng/api';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { GetValueFromFahrenheit, GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';
import { Store } from '@ngneat/elf';
import { UsersService } from 'src/app/core/services/users-service';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';

export interface CementSlurriesForm {
  name: string;
  type: string;
  density: number;
  mixWaterDensity: number;
  densityTemperature: number;
  fann600: number;
  fann300: number;
  fann200: number;
  fann100: number;
  fann6: number;
  fann3: number;
  rheologyTemperature: number;
}

@Component({
    selector: 'app-cement-slurries',
    templateUrl: './cement-slurries.component.html',
    styles: [``],
    providers: subformComponentProviders(CementSlurriesComponent),
    standalone: false
})
export class CementSlurriesComponent implements OnChanges, AfterViewInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }

  constructor(private _usersService: UsersService) {}

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public fluidsStore: Store;

  public isLoading = signal<boolean>(false);
  public tablePlot: SelectItem[];
  public showPlot: boolean;
  public plot = {
    data: [],
    layout: {},
    config: {}
  };

  public yAxisTitle = 'Shear Stress, τ (lbf / ft²)';
  public xAxisTitle = 'Shear Rate, γ (1/s)';
  public plotName = 'cementSlurriesPlot';
  public downloadPlotName = 'slurry_rheology_plot';

  private e = 20;
  private tauBinghamArray: number[];
  private gammaPlotArray: number[] = [0, 5, 10, 15, 20, 40, 60, 80, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200];
  private tauRegressionArray: number[];
  private dataArray: any[];

  public plasticViscosity: number;
  public yieldPoint: number;
  public kPrime: number;
  public nPrime: number;
  private plasticViscosityFieldUnit!: number;
  private yieldPointFieldUnit!: number;
  private kPrimeFieldUnit!: number;

  public densityUnits: string;
  public tempUnit: string;
  public kPrimeUnit: string;
  public plasticViscosityUnit: string;
  public yieldPointUnit: string;
  public mixWaterDensityValidation: { min: number, max: number };
  public densityValidation: { min: number, max: number };
  public temperatureValidation: { min: number, max: number };

  public form = createForm<CementSlurriesForm>(this, {
    formType: FormType.SUB,
    formControls: {
      name: new UntypedFormControl(null),
      type: new UntypedFormControl(null),
      density: new UntypedFormControl(null, { updateOn: 'blur' }),
      mixWaterDensity: new UntypedFormControl(null, { updateOn: 'blur' }),
      densityTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      fann600: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.min(0.1), Validators.max(500)] }),
      fann300: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.min(0.1), Validators.max(500)] }),
      fann200: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.min(0.1), Validators.max(500)] }),
      fann100: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.min(0.1), Validators.max(500)] }),
      fann6: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.min(0.1), Validators.max(500)] }),
      fann3: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.min(0.1), Validators.max(500)] }),
      rheologyTemperature: new UntypedFormControl(null, { updateOn: 'blur' })
    }
  });

  ngOnChanges(): void {
    if (!this.userUnits) {
      return;
    }
    this.tempUnit = this.userUnits.temperature;
    this.densityUnits = unitsLib[this.userUnits.density].symbol;
    this.kPrimeUnit = unitsLib[this.userUnits.kPrime]?.symbol;
    this.plasticViscosityUnit = unitsLib[this.userUnits.plasticViscosity].symbol;
    this.yieldPointUnit = unitsLib[this.userUnits.yieldPoint].symbol;

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: 32, max: 500 };
        break;
      case '°C':
        this.temperatureValidation = { min: 0, max: 260 };
        break;
      case 'K':
        this.temperatureValidation = { min: 273.15, max: 533.15 };
        break;
    }

    this.form.formGroup.controls.densityTemperature.setValidators([Validators.required, Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max)]);
    this.form.formGroup.controls.rheologyTemperature.setValidators([Validators.required, Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max)]);

    switch (this.densityUnits) {
      case 'ppg':
        this.densityValidation = { min: 0.1, max: 25 };
        this.mixWaterDensityValidation = { min: 8.33, max: 25 };
        break;
      case 'kg/m³':
      case 'g/L':
        this.densityValidation = { min: 10, max: 2995 };
        this.mixWaterDensityValidation = { min: 998.1, max: 2995.6 };
        break;
      case 'g/cm³':
      case 'kg/l':
      case 'sg':
        this.densityValidation = { min: 0.01, max: 3 };
        this.mixWaterDensityValidation = { min: 0.998, max: 2.9956 };
        break;
      case 'g/m³':
        this.densityValidation = { min: 11982, max: 2995660 };
        this.mixWaterDensityValidation = { min: 998154, max: 2995660 };
        break;
    }

    this.form.formGroup.controls.density.setValidators([Validators.required, Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max)]);
    this.form.formGroup.controls.mixWaterDensity.setValidators([Validators.required, Validators.min(this.mixWaterDensityValidation.min), Validators.max(this.mixWaterDensityValidation.max)]);
  }

  private async calculateNpKp() {
    let Sx = 0, Sy = 0, Sxx = 0, Sxy = 0, n = 0;
    this.dataArray = [];
    Object.entries(this.form.formGroup.controls).forEach(control => {
      if ((control[0] === 'fann600' || control[0] === 'fann300' || control[0] === 'fann200'
        || control[0] === 'fann100' || control[0] === 'fann6' || control[0] === 'fann3') && +control[1].value > 0.00001) {
        let shearStress = +control[1].value;
        let shearRate = +control[0].substring(4);
        const gamma = this.convertRpmTo1PerSecond(shearRate);
        const tau = this.convertFannReadingToFieldUnits(shearStress);

        this.dataArray.push(
          {
            gamma: gamma,
            tau: tau
          }
        )

        if (tau != 0) {
          shearRate = Math.log(gamma);
          shearStress = Math.log(tau);
        }

        // Sumation terms
        Sx += shearRate;
        Sy += shearStress;
        Sxx += Math.pow(shearRate, 2);
        Sxy += shearRate * shearStress;
        n++;
      }
    });

    const b = (Sxy - Sx * Sy / n) / (Sxx - Sx * Sx / n);
    const a = (Sy - b * Sx) / n;

    //Since data has been transformed to logs, then a must be transformed back
    this.kPrimeFieldUnit = Math.exp(a);
    this.nPrime = b;
    this.kPrime = this.kPrimeFieldUnit;

    if (this.kPrimeUnit !== "lb*s^n'/ft²") {
      this.kPrime = await lastValueFrom(this._usersService.convertUnits(this.kPrimeFieldUnit, 'UnitKPrime', this.kPrimeUnit, "lb*s^n'/ft²"));
    }
  }

  private GetTauBinghamPlotArray() {
    this.tauBinghamArray = [];
    this.tauBinghamArray.push(this.yieldPointFieldUnit / 100);

    for (let i = 1; i < this.e; i++) {
      this.tauBinghamArray.push(this.tauBinghamArray[i - 1] + this.plasticViscosityFieldUnit * (this.gammaPlotArray[i] - this.gammaPlotArray[i - 1]) / 47880.259);
    }
  }

  private async calculatePvYp() {
    //Approximate Bingham Plastic parameters PV and YP with linear approximate based on simulated R300 and R600 Fann readings.
    const gam300 = this.convertRpmTo1PerSecond(300);
    const gam600 = this.convertRpmTo1PerSecond(600);
    const tau300 = this.kPrimeFieldUnit * Math.pow(gam300, this.nPrime);
    const tau600 = this.kPrimeFieldUnit * Math.pow(gam600, this.nPrime);

    //Calculate PV in lbf-s/ft2 as slope of Bingham-Plastic line
    const pv = (tau600 - tau300) / (gam600 - gam300);

    //Calculate YP as slope-intercept of Bingham-Plastic line
    const yp = tau300 - pv * gam300;

    //Convert PV to centiPoise, cP
    this.plasticViscosityFieldUnit = +(pv * 47880.259).toFixed(3);
    this.plasticViscosity = this.plasticViscosityFieldUnit;

    //Convert YP to lbf/100ft2
    this.yieldPointFieldUnit = +(yp * 100).toFixed(3);
    this.yieldPoint = this.yieldPointFieldUnit;

    if (this.plasticViscosityUnit !== "cP") {
      this.plasticViscosity = +(await lastValueFrom(this._usersService.convertUnits(this.plasticViscosityFieldUnit, 'UnitPlasticViscosity', this.plasticViscosityUnit, "cP"))).toFixed(3);
    }

    if (this.yieldPointUnit !== "lbf/100ft²") {
      this.yieldPoint = +(await lastValueFrom(this._usersService.convertUnits(this.yieldPointFieldUnit, 'UnitYieldPoint', this.yieldPointUnit, "lbf/100ft²"))).toFixed(3);
    }
  }

  private convertRpmTo1PerSecond(shearRateInRpm) {
    return shearRateInRpm * 1.703;
  }

  private convertFannReadingToFieldUnits(shearStress) {
    return shearStress * 1.067 / 100.0;
  }

  public async calculateRheologyProperties(): Promise<void> {
    this.isLoading.set(true);
    try {
      await this.calculateNpKp();
      await this.calculatePvYp();
      this.GetTauRegressionFitArray();
      this.GetTauBinghamPlotArray();
      this.plotData();
    } finally {
      this.isLoading.set(false);
    }
  }

  tablePlotToggle() {
    this.showPlot = !(this.showPlot ?? false);
  }

  ngAfterViewInit(): void {
    this.form.controlValue$.pipe(takeUntil(this._destroy$)).subscribe(value => {
      if (value) {
        void this.calculateRheologyProperties();
      }
    });
  }

  public applyDefaults(): void {
    const densityDefault = GetValueFromPpg(15.8, this.densityUnits);
    const mixWaterDefault = GetValueFromPpg(8.33, this.densityUnits);
    const densityTempDefault = GetValueFromFahrenheit(120, this.tempUnit);
    const Fann300_default = 85;
    const Fann200_default = 74;
    const rheologyTempDefault = GetValueFromFahrenheit(120, this.tempUnit);

    this.form.formGroup.controls.density.setValue(densityDefault);
    this.form.formGroup.controls.mixWaterDensity.setValue(mixWaterDefault);
    this.form.formGroup.controls.densityTemperature.setValue(densityTempDefault);
    this.form.formGroup.controls.fann300.setValue(Fann300_default);
    this.form.formGroup.controls.fann200.setValue(Fann200_default);
    this.form.formGroup.controls.rheologyTemperature.setValue(rheologyTempDefault);
  }

  private GetTauRegressionFitArray(): number[] {
    this.tauRegressionArray = [];

    for (let i = 0; i < this.e; i++) {
      this.tauRegressionArray.push(this.kPrimeFieldUnit * Math.pow(this.gammaPlotArray[i], this.nPrime));
    }

    return this.tauRegressionArray;
  }

  private plotData() {
    const plot = [];

    // Power-Law
    const powerLawArray = (
      {
        name: 'Power-Law',
        x: this.gammaPlotArray,
        y: this.tauRegressionArray,
        line: {
          dash: 'solid',
          // color: '#E69138'
        }
      }
    )
    plot.push(powerLawArray);

    // Bingham-Plastic
    const binghamPlasticArray = (
      {
        name: 'Bingham-Plastic',
        x: this.gammaPlotArray,
        y: this.tauBinghamArray,
        line: {
          dash: 'dash',
          // color: '#0000FF'
        }
      }
    )
    plot.push(binghamPlasticArray);

    // Data
    const gammaValues = this.dataArray.map(x => x.gamma);
    const tauValues = this.dataArray.map(x => x.tau);
    const dataArray = (
      {
        name: 'Data',
        x: gammaValues,
        y: tauValues,
        mode: 'markers'
      }
    )
    plot.push(dataArray);

    this.plot.data = plot;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
